import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Canvas, type ThreeEvent } from '@react-three/fiber';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  Focus,
  Info,
  Loader2,
  Redo2,
  RotateCw,
  Save,
  Trash2,
  Undo2,
  Wand2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { cantidadDe, formatoCantidad, formatoPorcentaje } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { useContainerLayout } from '@/features/import-tracking/hooks/use-container-layout';
import {
  describeProblem,
  type LayoutBox,
  type PlacementCheck,
} from '@/features/import-tracking/utils/container-layout';
import { comerciaService } from '../../services/comercia-service';
import { useImportacion } from '../../store/importacion-store';
import type {
  ContenedorDeComercia,
  ImportacionDeComercia,
  ProductoDeComercia,
} from '../../types/comercia.types';
import {
  DESBALANCE,
  apoyaSobreFragil,
  autoAcomodar,
  equilibrar,
  importaElReparto,
  libreEnElLargo,
  limitesDe,
  lineasParaGuardar,
  pesoAlFondo,
  productoDeUnidad,
  secuenciaDeCarga,
  ubicarSinMover,
  unidadesDelContenedor,
} from '../../utils/carga';
import { formatoFijo } from '@/lib/utils/formato-precio';
import { EscenaDeCarga } from './escena-de-carga';

/** Paso del acomodo fino con las flechas, en cm (con Mayúscula, cinco veces más). */
const PASO_CM = 5;

/**
 * El acomodo de UN contenedor (maqueta de Melanie, Comercia › Carga): la lista de
 * productos a la izquierda —cuántos van ubicados, «Ubicar 20», arrastrar al
 * contenedor—, el volumen y el peso, lo que hay que revisar y la secuencia de
 * carga; a la derecha, el contenedor en 3D con su barra de herramientas.
 *
 * Se guarda solo (un segundo y medio después del último cambio) y también con
 * «Guardar acomodo». El motor es el del editor de la app (`useContainerLayout`):
 * paredes, choques, peso máximo y gravedad, con deshacer y rehacer.
 */
export function EditorDeCarga({
  importacion,
  contenedor,
  onActualizada,
}: {
  importacion: ImportacionDeComercia;
  contenedor: ContenedorDeComercia;
  onActualizada: (imp: ImportacionDeComercia | null) => void;
}) {
  const { toast } = useToast();
  const limites = useMemo(() => limitesDe(contenedor.tipo), [contenedor.tipo]);
  const productos = importacion.productos;
  const porId = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);
  const fragiles = useMemo(
    () => new Set(productos.filter((p) => p.fragil).map((p) => p.id)),
    [productos]
  );

  const inicial = useMemo(
    () => unidadesDelContenedor(productos, importacion.contenedores, contenedor.id),
    // Se rearma sólo si cambia la carga, no las posiciones (eso ya lo tiene el editor).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firmaDeLaCarga(importacion, contenedor)]
  );
  const layout = useContainerLayout(inicial.placed, inicial.tray, limites, 1);
  const firma = firmaDeLaCarga(importacion, contenedor);
  const firmaAnterior = useRef(firma);
  useEffect(() => {
    if (firmaAnterior.current === firma) return;
    firmaAnterior.current = firma;
    layout.reset(inicial.placed, inicial.tray);
    setSeleccionada(null);
    // `layout` cambia en cada render: lo que importa es la firma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma]);

  const [seleccionada, setSeleccionada] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const [fantasma, setFantasma] = useState<{ box: LayoutBox; check: PlacementCheck } | null>(null);
  const [acomodando, setAcomodando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [soltandoEncima, setSoltandoEncima] = useState(false);
  /** Cambia para volver a encuadrar la cámara (botón «Vista inicial»). */
  const [vista, setVista] = useState(0);
  const desfase = useRef({ x: 0, y: 0 });
  const pisoDesdePantalla = useRef<((x: number, y: number) => [number, number] | null) | null>(
    null
  );

  const colorDe = useCallback(
    (id: string) => porId.get(productoDeUnidad(id))?.color ?? '#64748b',
    [porId]
  );
  const rotuloDe = useCallback(
    (id: string) => porId.get(productoDeUnidad(id))?.descripcion ?? 'Carga',
    [porId]
  );

  // ── Guardar ──
  const guardar = useCallback(async () => {
    setGuardando(true);
    setError(null);
    // Lo que se manda: si se mueve algo mientras tanto, eso sigue pendiente.
    const enviado = { placed: layout.placed, tray: layout.tray };
    try {
      const r = await comerciaService.guardarAcomodo(
        contenedor.id,
        lineasParaGuardar(enviado.placed)
      );
      layout.markSaved(enviado);
      onActualizada(r.importacion);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo guardar el acomodo. Vuelve a intentar.'));
    } finally {
      setGuardando(false);
    }
  }, [contenedor.id, layout, onActualizada]);

  // Se guarda solo, después del último cambio: el trabajo no se pierde al salir.
  useEffect(() => {
    if (!layout.dirty || arrastrando) return;
    const t = setTimeout(() => void guardar(), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.placed, layout.dirty, arrastrando]);

  /*
    Al irse antes de ese segundo y medio («Continuar», el menú, otro
    contenedor), lo pendiente se guarda igual.

    🔴 Antes el temporizador se cancelaba al salir y el cambio se perdía sin
    aviso: quitar un monitor y seguir enseguida dejaba el acomodo de antes
    (auditoría del 29-oct).
  */
  const guardarAlSalir = useRef<() => void>(() => undefined);
  useEffect(() => {
    guardarAlSalir.current = () => {
      if (!layout.dirty) return;
      // Si se fue porque se quitó el contenedor, no hay nada que guardar.
      const sigue = useImportacion
        .getState()
        .importacion?.contenedores.some((c) => c.id === contenedor.id);
      if (!sigue) return;
      comerciaService
        .guardarAcomodo(contenedor.id, lineasParaGuardar(layout.placed))
        .then((r) => onActualizada(r.importacion))
        .catch((e) =>
          toast({
            title: 'No se pudo guardar el último cambio del acomodo',
            description: mensajeDeError(e, 'Vuelve a Carga y revísalo.'),
            variant: 'destructive',
          })
        );
    };
  });
  useEffect(() => () => guardarAlSalir.current(), []);

  // Cerrar o recargar la pestaña con algo sin guardar: el aviso del navegador.
  useEffect(() => {
    if (!layout.dirty) return;
    const avisar = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [layout.dirty]);

  // ── Arrastre dentro del contenedor ──
  /*
    El soltar se escucha desde el MISMO momento en que se agarra la caja.

    🔴 Antes lo escuchaba el plano de arrastre en un efecto, que se engancha
    después de dibujar: con un clic rápido (bajar y subir el mouse enseguida) el
    soltar llegaba antes y el editor se quedaba «arrastrando» — sin guardar solo,
    sin la ficha de la caja y con la cámara trabada.
  */
  const soltarRef = useRef<() => void>(() => undefined);
  const empezarArrastre = useCallback(
    (id: string, e: ThreeEvent<PointerEvent>) => {
      const b = layout.placed.find((x) => x.id === id);
      if (!b) return;
      desfase.current = { x: e.point.x * 100 - b.x, y: e.point.z * 100 - b.y };
      setArrastrando(id);
      document.body.style.cursor = 'grabbing';
      const terminar = () => {
        window.removeEventListener('pointerup', terminar);
        window.removeEventListener('pointercancel', terminar);
        soltarRef.current();
      };
      window.addEventListener('pointerup', terminar);
      window.addEventListener('pointercancel', terminar);
    },
    [layout.placed]
  );
  const moverArrastre = useCallback(
    (x: number, y: number) => {
      if (!arrastrando) return;
      setFantasma(layout.preview(arrastrando, x - desfase.current.x, y - desfase.current.y));
    },
    [arrastrando, layout]
  );
  const soltar = useCallback(() => {
    if (arrastrando && fantasma?.check.valid)
      layout.moveTo(arrastrando, fantasma.box.x, fantasma.box.y);
    setArrastrando(null);
    setFantasma(null);
    document.body.style.cursor = 'auto';
  }, [arrastrando, fantasma, layout]);
  useEffect(() => {
    soltarRef.current = soltar;
  }, [soltar]);

  // ── Teclado ──
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable))
        return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        layout.undo();
        return;
      }
      if (ctrl && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        e.preventDefault();
        layout.redo();
        return;
      }
      if (!seleccionada) return;
      const paso = e.shiftKey ? PASO_CM * 5 : PASO_CM;
      const acciones: Record<string, () => void> = {
        r: () => layout.rotate(seleccionada),
        R: () => layout.rotate(seleccionada),
        ArrowLeft: () => layout.nudge(seleccionada, -paso, 0),
        ArrowRight: () => layout.nudge(seleccionada, paso, 0),
        ArrowUp: () => layout.nudge(seleccionada, 0, -paso),
        ArrowDown: () => layout.nudge(seleccionada, 0, paso),
        Delete: () => {
          layout.unplace(seleccionada);
          setSeleccionada(null);
        },
        Backspace: () => {
          layout.unplace(seleccionada);
          setSeleccionada(null);
        },
        Escape: () => setSeleccionada(null),
      };
      const accion = acciones[e.key];
      if (accion) {
        e.preventDefault();
        accion();
      }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [seleccionada, layout]);

  // ── Acciones ──
  const autoAcomodarTodo = () => {
    setAcomodando(true);
    // Deja pintar «Acomodando…» antes de calcular (con cientos de bultos tarda).
    setTimeout(() => {
      try {
        const r = autoAcomodar([...layout.placed, ...layout.tray], limites, fragiles);
        layout.applyLayout(r.placed, r.tray);
        setSeleccionada(null);
        if (r.tray.length > 0) {
          const hayFragiles = r.tray.some((b) => fragiles.has(productoDeUnidad(b.id)));
          toast({
            title: `${cantidadDe(r.tray.length, 'bulto no entra', 'bultos no entran')} en este contenedor`,
            description: hayFragiles
              ? 'Sobre lo frágil sólo se apila el mismo producto. Agrega otro contenedor o quita «frágil» al producto si aguanta otra carga encima.'
              : 'Agrega otro contenedor para ubicarlos.',
          });
        }
      } finally {
        setAcomodando(false);
      }
    }, 30);
  };

  const ubicarProducto = (productoId: string, cuantas?: number) => {
    const libres = layout.tray.filter((b) => productoDeUnidad(b.id) === productoId);
    const elegidas = cuantas ? libres.slice(0, cuantas) : libres;
    if (elegidas.length === 0) return;
    const r = ubicarSinMover(layout.placed, elegidas, limites, fragiles);
    const puestas = new Set(r.placed.map((b) => b.id));
    layout.applyLayout(
      r.placed,
      layout.tray.filter((b) => !puestas.has(b.id))
    );
    if (r.sinLugar.length > 0)
      toast({
        title: `No hay lugar para ${cantidadDe(r.sinLugar.length, 'bulto', 'bultos')}`,
        description: 'Prueba «Auto-acomodar» o agrega otro contenedor.',
      });
  };

  const equilibrarPeso = () => {
    const r = equilibrar(layout.placed, limites);
    if (r.movido === 0) {
      toast({
        title: 'No se puede equilibrar corriendo la carga',
        description: 'La carga ocupa todo el largo: reparte los productos pesados a mano.',
      });
      return;
    }
    layout.applyLayout(r.placed, layout.tray);
  };

  // Soltar un producto de la lista sobre el contenedor: una unidad donde cayó.
  const alSoltar = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setSoltandoEncima(false);
    const productoId = e.dataTransfer.getData('text/x-comercia-producto');
    if (!productoId) return;
    const libre = layout.tray.find((b) => productoDeUnidad(b.id) === productoId);
    if (!libre) return;
    const punto = pisoDesdePantalla.current?.(e.clientX, e.clientY);
    if (
      punto &&
      layout.place(libre.id, punto[0] - libre.lengthCm / 2, punto[1] - libre.widthCm / 2)
    ) {
      setSeleccionada(libre.id);
      return;
    }
    ubicarProducto(productoId, 1);
  };

  // ── Lo que se muestra ──
  const enEste = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of layout.placed)
      m.set(productoDeUnidad(b.id), (m.get(productoDeUnidad(b.id)) ?? 0) + 1);
    return m;
  }, [layout.placed]);
  const enOtros = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of importacion.contenedores)
      if (c.id !== contenedor.id)
        for (const l of c.lineas) m.set(l.productoId, (m.get(l.productoId) ?? 0) + l.cantidad);
    return m;
  }, [importacion.contenedores, contenedor.id]);

  const fondo = pesoAlFondo(layout.placed, limites.lengthCm);
  const desbalanceado =
    importaElReparto(layout.stats.usedWeightKg, layout.stats.maxWeightKg) &&
    (fondo > DESBALANCE || fondo < 100 - DESBALANCE);
  const libreCm = libreEnElLargo(layout.placed, limites.lengthCm);
  const sobreFragil = layout.placed.filter((b) =>
    apoyaSobreFragil(b, layout.placed, fragiles)
  ).length;
  const sinMedidas = productos.filter((p) => !(p.largoCm && p.anchoCm && p.altoCm));
  const secuencia = secuenciaDeCarga(layout.placed);
  const { stats } = layout;

  const pendientesPorProducto = productos
    .map((p) => ({ p, n: layout.tray.filter((b) => productoDeUnidad(b.id) === p.id).length }))
    .filter((x) => x.n > 0);
  const revisar: Array<{
    clave: string;
    texto: string;
    accion?: { texto: string; hacer: () => void };
    aviso?: boolean;
    /** El color del producto, para los bultos sin ubicar. */
    color?: string;
  }> = [
    ...pendientesPorProducto.map(({ p, n }) => ({
      clave: `pend-${p.id}`,
      texto: `${formatoCantidad(n)} × ${p.descripcion} sin ubicar${
        p.fragil ? ' (frágil: encima sólo admite el mismo producto)' : ''
      }`,
      accion: { texto: 'Ubicar', hacer: () => ubicarProducto(p.id) },
      color: p.color,
    })),
    /*
      El aire a lo largo se avisa siempre que pase de 30 cm: es lo que en la
      práctica hace que la carga se corra y se golpee en el viaje.
    */
    ...(layout.placed.length > 0 && libreCm >= 30
      ? [
          {
            clave: 'libre',
            texto: `Quedan ${formatoFijo(libreCm / 100, 1)} m libres a lo largo: traba la carga (tablas o bolsas de aire) para que no se corra`,
          },
        ]
      : []),
    ...(desbalanceado
      ? [
          {
            clave: 'peso',
            texto:
              fondo > DESBALANCE
                ? `${formatoPorcentaje(fondo, { decimales: 0 })} del peso al fondo`
                : `${formatoPorcentaje(100 - fondo, { decimales: 0 })} del peso junto a la puerta`,
            accion: { texto: 'Equilibrar', hacer: equilibrarPeso },
            aviso: true,
          },
        ]
      : []),
    ...(stats.overweight
      ? [{ clave: 'sobrepeso', texto: 'Supera el peso máximo del contenedor', aviso: true }]
      : []),
    ...(sobreFragil > 0
      ? [
          {
            clave: 'fragil',
            texto: `${cantidadDe(sobreFragil, 'bulto apoyado', 'bultos apoyados')} sobre algo frágil`,
            accion: { texto: 'Reacomodar', hacer: autoAcomodarTodo },
            aviso: true,
          },
        ]
      : []),
  ];

  const seleccionadaCaja = seleccionada ? layout.placed.find((b) => b.id === seleccionada) : null;

  return (
    // La columna de la maqueta (349 px en la captura al 125 %): más ancho para el contenedor.
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[290px_minmax(0,1fr)]">
      {/* ── Columna izquierda ── */}
      <div className="space-y-5">
        <section aria-label="Productos" className="rounded-tarjeta border bg-card p-5 shadow-sm">
          <h3 className="font-titulo text-lg font-semibold">Productos</h3>
          <ul className="mt-3 divide-y">
            {productos.map((p) => (
              <FilaDeProducto
                key={p.id}
                producto={p}
                ubicadas={(enEste.get(p.id) ?? 0) + (enOtros.get(p.id) ?? 0)}
                libres={layout.tray.filter((b) => productoDeUnidad(b.id) === p.id).length}
                onUbicar={(n) => ubicarProducto(p.id, n)}
              />
            ))}
          </ul>
          <p className="mt-3 text-sm text-muted-foreground">
            Arrastra un producto al contenedor para ubicarlo.
          </p>
        </section>

        <section
          aria-label="Ocupación del contenedor"
          className="space-y-4 rounded-tarjeta border bg-card p-5 shadow-sm"
        >
          <Medidor
            nombre="Volumen"
            // La capacidad nominal del tipo (33,2 m³ en un 20'), la misma de la tarjeta
            // de arriba: el producto de las medidas interiores daba 33,1.
            valor={`${formatoFijo(stats.usedVolumeCbm, 2)} / ${formatoFijo(contenedor.tipo.volumenM3 || stats.containerVolumeCbm, 1)} m³`}
            porcentaje={stats.fillPercent}
          />
          <Medidor
            nombre="Peso"
            valor={`${formatoCantidad(stats.usedWeightKg)} / ${formatoCantidad(stats.maxWeightKg)} kg`}
            porcentaje={stats.weightPercent}
            peligro={stats.overweight}
          />

          {revisar.length > 0 && (
            // Los tonos de la maqueta, medidos: durazno de fondo y naranja quemado.
            <div className="rounded-xl bg-[#FDEFE6] p-4 dark:bg-alerta-suave/70">
              <p className="text-xs font-bold tracking-[0.1em] text-[#9A3412] dark:text-alerta">
                PARA REVISAR · {revisar.length}
              </p>
              <ul className="mt-2 space-y-2">
                {revisar.map((r) => (
                  <li key={r.clave} className="flex items-center gap-3 text-sm">
                    {r.aviso ? (
                      <AlertTriangle
                        className="h-4 w-4 shrink-0 text-[#9A3412] dark:text-alerta"
                        aria-hidden="true"
                      />
                    ) : r.color ? (
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: r.color }}
                        aria-hidden="true"
                      />
                    ) : (
                      <Info className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                    <span className="min-w-0 flex-1">{r.texto}</span>
                    {r.accion && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 rounded-full bg-card"
                        onClick={r.accion.hacer}
                      >
                        {r.accion.texto}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {sinMedidas.length > 0 && (
            <p className="text-sm text-bloqueo">
              {cantidadDe(sinMedidas.length, 'producto no tiene', 'productos no tienen')} medidas y
              no se puede ubicar.{' '}
              <Link to="/comercia/productos" className="font-semibold underline">
                Completar
              </Link>
            </p>
          )}

          {secuencia.length > 0 && (
            <div>
              <h4 className="text-[15px] font-semibold">Secuencia de carga</h4>
              <ol className="mt-2 space-y-2">
                {secuencia.map((t, i) => (
                  <li key={t.productoId} className="flex items-center gap-3 text-sm">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold tabular-nums">
                      {i + 1}
                    </span>
                    <span
                      className="h-3 w-3 shrink-0 rounded-sm"
                      style={{ backgroundColor: porId.get(t.productoId)?.color }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {porId.get(t.productoId)?.descripcion}
                      {t.lugar !== 'medio' && (
                        <span className="text-muted-foreground"> ({t.lugar})</span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      ×{formatoCantidad(t.cantidad)}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="mt-2 text-xs text-muted-foreground">
                Del fondo a la puerta, de abajo hacia arriba.
              </p>
            </div>
          )}
        </section>
      </div>

      {/* ── El contenedor ── */}
      <section
        aria-label="Plano de carga"
        className="overflow-hidden rounded-tarjeta border bg-card shadow-sm"
      >
        <div className="flex flex-wrap items-center gap-2 border-b px-5 py-4">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={autoAcomodarTodo}
            disabled={acomodando}
          >
            {acomodando ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Wand2 className="h-4 w-4" aria-hidden="true" />
            )}
            {acomodando ? 'Acomodando…' : 'Auto-acomodar'}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => seleccionada && layout.rotate(seleccionada)}
            disabled={!seleccionada}
          >
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            Rotar
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => {
              if (!seleccionada) return;
              layout.unplace(seleccionada);
              setSeleccionada(null);
            }}
            disabled={!seleccionada}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Quitar
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={layout.undo}
            disabled={!layout.canUndo}
            aria-label="Deshacer"
          >
            <Undo2 className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={layout.redo}
            disabled={!layout.canRedo}
            aria-label="Rehacer"
          >
            <Redo2 className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full"
            onClick={() => setVista((v) => v + 1)}
            aria-label="Vista inicial"
            title="Volver a la vista inicial del contenedor"
          >
            <Focus className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            variant="degradado"
            className="ml-auto rounded-full px-6"
            onClick={() => void guardar()}
            disabled={guardando || !layout.dirty}
          >
            {guardando ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : layout.dirty ? (
              <Save className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Check className="h-4 w-4" aria-hidden="true" />
            )}
            {guardando ? 'Guardando…' : layout.dirty ? 'Guardar acomodo' : 'Acomodo guardado'}
          </Button>
        </div>

        {error && (
          <p
            role="alert"
            className="mx-5 mt-3 rounded-lg bg-bloqueo-suave px-3 py-2 text-sm text-bloqueo"
          >
            {error}
          </p>
        )}

        <div
          className={cn(
            // El fondo de la maqueta, medido: un lavanda casi blanco.
            'relative h-[460px] bg-[#F6F5FA] dark:bg-muted/30 sm:h-[560px] xl:h-[640px]',
            soltandoEncima && 'ring-2 ring-inset ring-primary'
          )}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('text/x-comercia-producto')) {
              e.preventDefault();
              setSoltandoEncima(true);
            }
          }}
          onDragLeave={() => setSoltandoEncima(false)}
          onDrop={alSoltar}
        >
          <p className="pointer-events-none absolute left-4 top-3 z-10 rounded-lg border bg-card/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
            Arrastra para mover · <kbd className="font-semibold">R</kbd> rota · flechas ajustan ·{' '}
            <kbd className="font-semibold">Supr</kbd> quita
          </p>
          {/* `flat`: sin el ajuste de tonos de three.js, los colores salen tal cual. */}
          <Canvas
            flat
            dpr={[1, 2]}
            onPointerMissed={() => setSeleccionada(null)}
            aria-label="Contenedor en 3D"
          >
            <Suspense fallback={null}>
              <EscenaDeCarga
                limites={limites}
                cajas={layout.placed}
                colorDe={colorDe}
                rotuloDe={rotuloDe}
                seleccionada={seleccionada}
                arrastrando={arrastrando}
                fantasma={fantasma}
                soloLectura={false}
                onElegir={setSeleccionada}
                onEmpezarArrastre={empezarArrastre}
                onMoverArrastre={moverArrastre}
                pisoDesdePantalla={pisoDesdePantalla}
                encuadre={`${contenedor.id}:${contenedor.tipo.id}:${vista}`}
              />
            </Suspense>
          </Canvas>
          {fantasma && !fantasma.check.valid && (
            <p className="pointer-events-none absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-bloqueo px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              {fantasma.check.problems.map(describeProblem).join(' · ')}
            </p>
          )}
          {seleccionadaCaja && !arrastrando && (
            <div className="absolute bottom-4 right-4 max-w-xs rounded-xl border bg-card/95 px-4 py-3 text-sm shadow-md">
              <p className="font-semibold">{rotuloDe(seleccionadaCaja.id)}</p>
              <p className="text-xs text-muted-foreground tabular-nums">
                {formatoCantidad(seleccionadaCaja.lengthCm)} ×{' '}
                {formatoCantidad(seleccionadaCaja.widthCm)} ×{' '}
                {formatoCantidad(seleccionadaCaja.heightCm)} cm ·{' '}
                {formatoFijo(seleccionadaCaja.weightKg, 1)} kg
                {seleccionadaCaja.rotated ? ' · rotada' : ''}
              </p>
            </div>
          )}
          {layout.placed.length === 0 && (
            <p className="pointer-events-none absolute inset-x-0 bottom-6 text-center text-sm text-muted-foreground">
              El contenedor está vacío: usa «Auto-acomodar» o arrastra un producto.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

/** La identidad de la carga, sin posiciones: si cambia, el editor se rearma. */
function firmaDeLaCarga(imp: ImportacionDeComercia, contenedor: ContenedorDeComercia) {
  const productos = imp.productos
    .map((p) => `${p.id}:${p.cantidad}:${p.largoCm}x${p.anchoCm}x${p.altoCm}:${p.pesoUnitario}`)
    .join('|');
  const otros = imp.contenedores
    .filter((c) => c.id !== contenedor.id)
    .map((c) => `${c.id}:${c.lineas.map((l) => `${l.productoId}x${l.cantidad}`).join(',')}`)
    .join('|');
  return `${contenedor.id}:${contenedor.tipo.id}#${productos}#${otros}`;
}

function FilaDeProducto({
  producto: p,
  ubicadas,
  libres,
  onUbicar,
}: {
  producto: ProductoDeComercia;
  ubicadas: number;
  libres: number;
  onUbicar: (n?: number) => void;
}) {
  const completo = ubicadas >= p.cantidad;
  const arrastrable = libres > 0;
  return (
    <li
      draggable={arrastrable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/x-comercia-producto', p.id);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className={cn(
        'flex items-center gap-3 py-3',
        arrastrable && 'cursor-grab active:cursor-grabbing'
      )}
      title={arrastrable ? 'Arrástralo al contenedor' : undefined}
    >
      <span
        className="h-4 w-4 shrink-0 rounded"
        style={{ backgroundColor: p.color }}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        {/* En dos renglones si hace falta: cortado, «split 12.00…» no dice cuál es. */}
        <span
          className="line-clamp-2 block text-[14px] font-semibold leading-snug"
          title={p.descripcion}
        >
          {p.descripcion}
        </span>
        <span className={cn('block text-sm tabular-nums', completo ? 'text-ok' : 'text-alerta')}>
          {formatoCantidad(Math.min(ubicadas, p.cantidad))} de {formatoCantidad(p.cantidad)}{' '}
          ubicados
        </span>
      </span>
      {libres > 0 && (
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 rounded-full"
          onClick={() => onUbicar()}
        >
          Ubicar {formatoCantidad(libres)}
        </Button>
      )}
    </li>
  );
}

function Medidor({
  nombre,
  valor,
  porcentaje,
  peligro,
}: {
  nombre: string;
  valor: string;
  porcentaje: number;
  peligro?: boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[15px] font-semibold">{nombre}</span>
        <span className="text-sm text-muted-foreground tabular-nums">{valor}</span>
      </div>
      <div
        className="mt-2 h-2.5 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={nombre}
        aria-valuenow={Math.round(porcentaje)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-300',
            peligro ? 'bg-bloqueo' : 'bg-ok'
          )}
          style={{ width: `${Math.min(100, Math.max(porcentaje > 0 ? 2 : 0, porcentaje))}%` }}
        />
      </div>
    </div>
  );
}
